import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { UserWithRoles } from '@types';
import { muiTheme } from '@/theme/muiTheme';

export interface AssignedToFilterProps {
  value: string;
  onChange: (value: string) => void;
  users: UserWithRoles[];
  disabled?: boolean;
  size?: 'small' | 'medium';
}

export interface AssignedToOption {
  id: string;
  label: string;
  type: 'all' | 'me' | 'unassigned' | 'user';
  email?: string;
}

const COMPACT_HEIGHT = 32;

export const AssignedToFilter: React.FC<AssignedToFilterProps> = ({
  value,
  onChange,
  users,
  disabled = false,
  size = 'small',
}) => {
  // Build filter options
  const options = React.useMemo<AssignedToOption[]>(() => {
    const baseOptions: AssignedToOption[] = [
      { id: 'all', label: 'All Users', type: 'all' },
      { id: 'me', label: 'Assigned to Me', type: 'me' },
      { id: 'unassigned', label: 'Unassigned', type: 'unassigned' },
    ];

    // Add individual users
    const userOptions: AssignedToOption[] = users.map((user) => ({
      id: user.id,
      label: user.name || user.email || 'Unknown User',
      type: 'user',
      email: user.email,
    }));

    return [...baseOptions, ...userOptions];
  }, [users]);

  // Find the selected option
  const selectedOption = options.find((opt) => opt.id === value) || options[0];

  const handleChange = (_event: React.SyntheticEvent, newValue: AssignedToOption | null) => {
    onChange(newValue?.id || 'all');
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Autocomplete
        options={options}
        getOptionLabel={(option) => option.label}
        value={selectedOption}
        onChange={handleChange}
        disabled={disabled}
        disableClearable
        size={size}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Assigned To"
            variant="outlined"
            size={size}
            sx={{ 
              width: '100%',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
              },
              // Compact height to avoid header jump when toggling visibility
              '& .MuiOutlinedInput-root': {
                height: COMPACT_HEIGHT,
                paddingRight: '32px', // room for dropdown icon
              },
              '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
                padding: '6px 8px',
              },
              '& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment': {
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <div>
              <div className="font-medium">{option.label}</div>
              {option.type === 'user' && (
                <div className="text-xs text-gray-500">
                  {option.email || ''}
                </div>
              )}
            </div>
          </li>
        )}
        groupBy={(option) => {
          if (option.type === 'all' || option.type === 'me' || option.type === 'unassigned') {
            return 'Quick Filters';
          }
          return 'Users';
        }}
      />
    </ThemeProvider>
  );
};

export default React.memo(AssignedToFilter);
